import {
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  HostListener,
  Input,
  Output,
  ViewChild,
} from '@angular/core';
import { AbstractComponent, FocusableComponent } from '../../abstract.component';
import { SolIcon } from '@sol/icons';
import { SolIconComponent } from '../../core/sol-icon/sol-icon.component';
import { TranslatePipe } from '../../../pipes/translate.pipe';
import { SolShortcut } from '../../../services/shortcut.service';
import { SolShortcutComponent } from '../../core/sol-shortcut/sol-shortcut.component';
import { BrowserTools } from '../../../tools/browser-tools';
import { TimerTools } from '../../../tools/timer-tools';
import { SolButtonType } from '../../../models/core/sol-button';
import { SolPopoverMenuItemState } from './sol-popover-menu-item.state';

@Component({
  selector: 'sol-popover-menu-item',
  imports: [SolIconComponent, TranslatePipe, SolShortcutComponent],
  templateUrl: './sol-popover-menu-item.component.html',
  styles: [
    `
      :host {
        display: contents;
      }
    `,
  ],
})
export class SolPopoverMenuItemComponent extends AbstractComponent<SolPopoverMenuItemState> {
  // Computed
  protected readonly hostClass$ = this.compute(state => state.hostClass);
  protected readonly icon$ = this.compute(state => state.icon);
  protected readonly text$ = this.compute(state => state.text);
  protected readonly params$ = this.compute(state => state.params);
  protected readonly hint$ = this.compute(state => state.hint);
  protected readonly shortcut$ = this.compute(state => state.shortcut);
  protected readonly translatable$ = this.compute(state => state.translatable);

  // Binding
  @ViewChild('text')
  private textRef!: ElementRef<HTMLInputElement>;

  // Event
  @Output('selected')
  private readonly selectedEmitter = new EventEmitter<boolean>();

  constructor() {
    super(SolPopoverMenuItemState.of());

    // Focus on select
    this.watchComputed(
      state => state.selected,
      selected => {
        if (selected && this.state.autoFocus && !this.isFocused()) {
          TimerTools.nextTick(() => this.focus());
        }
      },
    );
  }

  @HostBinding('class') get hostClass(): string {
    return this.hostClass$();
  }

  @Input()
  public set type(type: SolButtonType | undefined) {
    this.update(state => state.withType(type ?? SolButtonType.secondary));
  }

  @Input()
  public set disabled(disabled: boolean) {
    this.update(state => state.withDisabled(disabled));
  }

  @Input()
  public set icon(icon: SolIcon | undefined) {
    this.update(state => state.withIcon(icon));
  }

  @Input()
  public set text(text: string) {
    this.update(state => state.withText(text));
  }

  @Input()
  public set params(params: Record<string, any> | undefined) {
    this.update(state => state.withParams(params ?? {}));
  }

  @Input()
  public set autoFocus(autoFocus: boolean) {
    this.update(state => state.withAutoFocus(autoFocus));
  }

  @Input()
  public set hint(hint: string | undefined) {
    this.update(state => state.withHint(hint || ''));
  }

  @Input()
  public set shortcut(shortcut: SolShortcut | undefined) {
    this.update(state => state.withShortcut(shortcut));
  }

  @Input()
  public set selected(selected: boolean) {
    this.update(state => state.withSelected(selected));
  }

  @Input()
  public set translatable(translatable: boolean) {
    this.update(state => state.withTranslatable(translatable));
  }

  protected override focusable(): FocusableComponent | undefined {
    return BrowserTools.toFocusable(this.textRef);
  }

  protected onFocused() {
    // Already up-to-date
    if (this.state.selected) {
      return;
    }

    // Update selected & emit
    this.update(state => state.withSelected(true));
    this.selectedEmitter.emit(true);
  }

  @HostListener('click', ['$event'])
  click(event: any) {
    if (this.state.disabled) {
      event.preventDefault();
    } else {
      // Noop: default event
    }
  }

  @HostListener('auxclick', ['$event'])
  auxclick(event: any) {
    if (this.state.disabled) {
      event.preventDefault();
    } else {
      // Noop: default event
    }
  }
}
